import { ENV } from '@/constants';

export const schemaObject_WatermarkRemover = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/watermark-remover',
			name: 'AI Watermark Remover — Erase Watermarks Online | Lift',
			description:
				"Remove watermarks with AI in one step. Lift's watermark remover AI detects logos, stamps, and overlays on any picture or clip and erases them automatically — no brushing, no masking. Try Lift now!",
			url: 'https://liftapp.ai/watermark-remover',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/watermark-remover#product' },
				{ '@id': 'https://liftapp.ai/watermark-remover#howto' },
				{ '@id': 'https://liftapp.ai/watermark-remover#benefits' },
				{ '@id': 'https://liftapp.ai/watermark-remover#faq' },
				{ '@id': 'https://liftapp.ai/watermark-remover#why-lift' },
				{ '@id': 'https://liftapp.ai/watermark-remover#explore-tools' },
				{ '@id': 'https://liftapp.ai/watermark-remover#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/watermark-remover#product',
			name: 'AI Watermark Remover',
			description:
				'Erase watermarks from photos and videos with Lift’s AI Watermark Remover. Upload a file, the AI finds every logo, stamp, or overlay on its own and rebuilds the background behind it — no brushing, no masking.',
			url: 'https://liftapp.ai/watermark-remover',
			fileFormat: [
				'image/jpeg',
				'image/png',
				'image/webp',
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
			],
			memoryRequirements: 'Max file size: 20MB for photos, 2GB for videos',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/watermark-remover#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Photo Or Video',
					text: 'Drop in an image or a clip from your device — all common photo and video formats are supported.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI Detect And Remove',
					text: 'Removal starts automatically: Lift locates every watermark, logo, or stamp and rebuilds the background behind it.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Review The Result',
					text: 'Compare photos with the before-and-after slider or play the cleaned video in the built-in player, and tap Retry for a fresh pass.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download Your Result',
					text: 'Save the clean file to your device in its original format, ready to post or share.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/watermark-remover#benefits',
			name: 'Who can benefit from AI Watermark Remover?',
			description:
				'Lift’s AI Watermark Remover helps content creators, online sellers, photographers, designers, and everyday users clear logos, stamps, and overlays from photos and videos in one pass.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Old channel stamps and app logos on your own footage and photos make a feed look inconsistent. Clear them before posting so every reel, story, and thumbnail looks polished.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Online sellers and marketers — Product visuals from supplier catalogs often arrive with a logo in the corner. Lift removes it in one pass, leaving clean images and clips ready for listings, ads, and campaigns.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Photographers and videographers — Proofs go out with a studio mark, but clients need the final version clean. Skip manual cloning and let AI restore the background behind the mark in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Designers and editors — Review copies and drafts usually carry an approval stamp. Once the version is signed off, Lift clears the mark so the asset drops straight into the layout or edit.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday users — A date stamp or a camera app logo can spoil a great memory. Wipe it away from any photo or video and keep the moment exactly as you remember it.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Watermark Remover by Lift',
				description:
					'Photo with a watermark next to the same photo after Lift’s watermark remover tool cleaned it.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/watermark-remover#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Watermark Remover?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "AI Watermark Remover is Lift's tool for deleting watermarks, logos, and stamps from both photos and videos. You upload a file, the AI detects every mark on its own, removes it, and reconstructs the background so the result looks as if the watermark was never there.",
					},
				},
				{
					'@type': 'Question',
					name: 'How do I remove a watermark from a photo or video?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your file to Lift and the removal begins automatically — there is nothing to select or outline. When the AI finishes, check the result, tap Retry if you want another pass, and download the clean version.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will the quality of my photo or video stay the same?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift only rebuilds the area under the watermark and leaves the rest of the picture or clip untouched, so colors, sharpness, and detail stay exactly as they were. The AI fills the cleared spot with matching texture and lighting, so there are no smudges or patches.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can Lift remove more than one watermark from the same photo or video?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI scans the whole photo or video and picks up every watermark, logo, or stamp it finds — not just the largest one. All of them are removed in a single pass, and the background behind each is rebuilt separately so the result stays seamless.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos and videos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your files securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/watermark-remover#why-lift',
			name: 'Why Lift AI Watermark Remover',
			description:
				'Discover the advantages of Lift AI Watermark Remover: high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Watermark Remover',
				description:
					'Clean video result after using Lift’s AI to remove a watermark.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/watermark-remover#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 1,
					name: 'AI Ghibli generator',
					url: 'https://liftapp.ai/ai-filters-ghibli',
					description:
						'Transform your photo into a Ghibli-style cartoon with AI.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 2,
					name: 'AI LEGO generator',
					url: 'https://liftapp.ai/ai-filters-lego',
					description: 'Turn your image into a LEGO-style illustration.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 3,
					name: 'AI Starter Pack generator',
					url: 'https://liftapp.ai/ai-filters-starter-pack',
					description:
						'Apply a modern stylized look with the Starter Pack aesthetic.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 4,
					name: 'AI Background Remover',
					url: 'https://liftapp.ai/background-remover',
					description: 'Erase and replace backgrounds in one click.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 5,
					name: 'AI Image Enhancer',
					url: 'https://liftapp.ai/image-enhancer',
					description: 'Enhance photo quality.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 6,
					name: 'AI Image Upscaler',
					url: 'https://liftapp.ai/image-upscaler',
					description:
						'Increase image resolution to HD without losing quality.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 7,
					name: 'AI Image Generator',
					url: 'https://liftapp.ai/image-gen',
					description:
						'Generate original artwork or portraits using text prompts.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 8,
					name: 'AI Headshot Generator',
					url: 'https://liftapp.ai/headshot-generator',
					description: 'Create a professional headshot with AI.',
				},
				{
					'@type': 'SoftwareApplication',
					operatingSystem: 'Web',
					applicationCategory: 'MultimediaApplication',
					position: 9,
					name: 'AI Watermark Remover',
					url: 'https://liftapp.ai/watermark-remover',
					description: 'Remove watermark from image with AI.',
				},
			],
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/watermark-remover#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Watermark Remover',
					item: 'https://liftapp.ai/watermark-remover',
				},
			],
		},
	],
};
